import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`The key goals are to create flexible, robust, and useful patterns that advance Primer's incremental innovation, while building upon an existing set of solid foundations.`}</p>
    <h2>{`Design system foundations`}</h2>
    <p>{`Primer is built on strong foundations that should remain stable. Become familiar with guidelines on typography, color, spacing, and layout.`}</p>
    <p>{`➡️ `}<a parentName="p" {...{
        "href": "/"
      }}>{`Primer design guidelines`}</a></p>
    <h2>{`Upcoming patterns and proposals`}</h2>
    <p>{`It's important to know what's coming into Primer, to avoid duplicated efforts and to accommodate upcoming changes to elements that may interact with what you're planning to work on.`}</p>
    <p>{`Check what is currently being designed and built, as this may introduce new concepts and variations to existing patterns.`}</p>
    <p>{`➡️ `}<a parentName="p" {...{
        "href": "https://github.com/github/primer/projects/1?fullscreen=true"
      }}>{`Primer backlog`}</a>{` (GitHub staff only)`}</p>
    <h2>{`Context and use cases`}</h2>
    <p>{`Before you start to create mockups and think of solutions, it's good to always carry out an audit of where similar patterns are used in github.com, and where it can potentially be used once a new pattern exists. You can also audit different products to see how they've solved similar problems.`}</p>
    <p>{`Think about as many different use cases where the pattern can be used across github.com as possible. The goal is to create patterns that are flexible, but that solve concrete problems.`}</p>
    <p>{`Finally, consider where a pattern will live in the context of the larger product. Don't design a single pattern in isolation of its possible surroundings.`}</p>
    <h2>{`Design mockups and prototypes`}</h2>
    <p>{`@todo Coming soon!`}</p>
    {/* Get started in Figma by duplicating the **Primer pattern template** file. You can find it in **Design Infrastructure > Contributions**. Follow the instructions in the file on how to name, and use the template, so that it accurately indicates the stage of the design work.
     You can also use an alternative prototyping tool like CodePen, Framer, or anything else that you're comfortable using — just make sure the prototypes are linked to from the pattern tracking issue. */}
    <p>{`➡️ `}<a parentName="p" {...{
        "href": "/guides/figma"
      }}>{`Using Figma at GitHub`}</a></p>
    {/* @todo Naming, API: design and eng alignment */}
    <h2>{`Share early, and often`}</h2>
    <p>{`The work that you're doing shouldn't be a surprise that you only reveal when you're done.`}</p>
    <p><strong parentName="p">{`Systems designers`}</strong>{` can provide extra context and considerations that are helpful to create a solid design.`}</p>
    <p><strong parentName="p">{`Product designers`}</strong>{` can consider the designs in the context of their own product area and whether their specific use cases could benefit from your proposal.`}</p>
    <p><strong parentName="p">{`Engineers`}</strong>{` can consider how something may be implemented, what is realistic within the existing constraints, and what is unfeasible.`}</p>
    <p>{`There are different ways you can ask for and get feedback:`}</p>
    <ul>
      <li parentName="ul">{`Async via `}<a parentName="li" {...{
          "href": "https://github.com/github/primer/discussions/categories/feedback"
        }}>{`discussions`}</a>{` (GitHub staff only): Record a short video and/or links to a Figma prototype (or any other kind of prototype, like sketches or code), providing context and specifying what kind of feedback you're looking for`}</li>
      <li parentName="ul">{`Primer open office hours: Attend office hours on Thursdays (1.30pm PT). The Zoom link is posted right before in #primer. Be prepared to give context and ask what kind of feedback you're looking for succinctly. Ideally, give a heads-up in #primer beforehand so we can be sure a designer will be present in the call`}</li>
      <li parentName="ul">{`Ad-hoc in real-time: In #primer, ask for a short critique session with a systems designer, to get feedback on a specific issue`}</li>
    </ul>
    <h2>{`Design checklist`}</h2>
    <p>{`All Primer design patterns should take into account the following areas:`}</p>
    <ul {...{
      "className": "contains-task-list"
    }}>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Primer Primitives`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Input types and devices (keyboard, pointer, touch, etc.)`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Responsiveness (from very small to extra large screens)`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Themeability`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Accessibility`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Content`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Interaction states`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Documentation/APIs`}</li>
      <li parentName="ul" {...{
        "className": "task-list-item"
      }}><input parentName="li" {...{
          "type": "checkbox",
          "checked": false,
          "disabled": true
        }}></input>{` `}{`Figma assets`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      